import { z } from "zod";

export const ROLES = ["owner", "editor", "reviewer", "viewer"] as const;
export const Role = z.enum(ROLES);
export type Role = z.infer<typeof Role>;

export const IDEA_MATURITIES = ["RAW", "CLARIFIED", "READY", "PROMOTED", "PARKED", "DISCARDED"] as const;
export const IdeaMaturity = z.enum(IDEA_MATURITIES);
export type IdeaMaturity = z.infer<typeof IdeaMaturity>;

export const WORK_ORDER_STATES = [
  "DRAFT",
  "SPEC_REVIEW",
  "PLAN_REVIEW",
  "READY",
  "RUNNING",
  "VERIFYING",
  "REVIEW_READY",
  "ACCEPTED",
  "STAGING",
  "RELEASE_APPROVAL",
  "RELEASED",
  "WAITING_HUMAN",
  "BLOCKED",
  "PAUSED_PROVIDER",
  "SUSPECT",
  "FAILED",
  "CANCELLED",
] as const;
export const WorkOrderState = z.enum(WORK_ORDER_STATES);
export type WorkOrderState = z.infer<typeof WorkOrderState>;

export const WORK_ITEM_STATES = [
  "not_started",
  "running",
  "unverified",
  "verifying",
  "verified",
  "failed",
  "blocked",
  "waived_by_human",
] as const;
export const WorkItemState = z.enum(WORK_ITEM_STATES);
export type WorkItemState = z.infer<typeof WorkItemState>;

export const AGENT_RUN_STATES = [
  "QUEUED",
  "LEASED",
  "RUNNING",
  "COMPLETED",
  "FAILED",
  "CANCELLED",
  "SUSPECT",
] as const;
export const AgentRunState = z.enum(AGENT_RUN_STATES);
export type AgentRunState = z.infer<typeof AgentRunState>;

/** Claude-Modelle, die ein Lauf nutzen kann; Kurzname wie in Claude Code (`--model`). */
export const CLAUDE_MODELS = ["fable", "opus", "sonnet"] as const;
export const ClaudeModel = z.enum(CLAUDE_MODELS);
export type ClaudeModel = z.infer<typeof ClaudeModel>;

/** Feste Modell-IDs je Kurzname, damit ein Lauf reproduzierbar bleibt und nicht dem jeweils neuesten Alias folgt. */
export const CLAUDE_MODEL_IDS: Record<ClaudeModel, string> = {
  fable: "claude-fable-5-1",
  opus: "claude-opus-5-5",
  sonnet: "claude-sonnet-5-5",
};

/** Aufwandsstufen von Claude Code (`--effort`), von sparsam bis gründlich. */
export const EFFORT_LEVELS = ["low", "medium", "high", "xhigh", "max"] as const;
export const EffortLevel = z.enum(EFFORT_LEVELS);
export type EffortLevel = z.infer<typeof EffortLevel>;

export const LANES = ["research", "design", "code", "test", "docs"] as const;
export const Lane = z.enum(LANES);
export type Lane = z.infer<typeof Lane>;

export const DECISION_CATEGORIES = [
  "product",
  "design",
  "architecture",
  "data_sharing",
  "security",
  "budget",
  "review",
  "release",
] as const;
export const DecisionCategory = z.enum(DECISION_CATEGORIES);
export type DecisionCategory = z.infer<typeof DecisionCategory>;

export const DECISION_STATES = ["PENDING", "RESOLVED", "EXPIRED", "INVALIDATED"] as const;
export const DecisionState = z.enum(DECISION_STATES);
export type DecisionState = z.infer<typeof DecisionState>;

export const NODE_STATES = ["ONLINE", "OFFLINE", "REVOKED"] as const;
export const NodeState = z.enum(NODE_STATES);
export type NodeState = z.infer<typeof NodeState>;

/** Lebensphase eines Projekts: in Entwicklung, im Betrieb (bekommt Updates) oder abgeschlossen. */
export const PROJECT_LIFECYCLES = ["development", "operating", "closed"] as const;
export const ProjectLifecycle = z.enum(PROJECT_LIFECYCLES);
export type ProjectLifecycle = z.infer<typeof ProjectLifecycle>;

/**
 * Auslieferungsweg eines Projekts (Stufe 1): `merge` mergt den Pull Request des Auftrags, `manual` mergt und
 * lässt den Owner danach nach Checkliste von Hand ausliefern (etwa Server, die er selbst per SSH betreibt).
 */
export const DELIVERY_MODES = ["merge", "manual"] as const;
export const DeliveryMode = z.enum(DELIVERY_MODES);
export type DeliveryMode = z.infer<typeof DeliveryMode>;

/** Wo ein Schritt für den Owner passiert („Für dich zu tun“, UX-12). */
export const OWNER_TASK_PLACES = ["vps", "plesk", "pc", "claude_app", "szyos", "other"] as const;
export const OwnerTaskPlace = z.enum(OWNER_TASK_PLACES);
export type OwnerTaskPlace = z.infer<typeof OwnerTaskPlace>;

export const PLATFORMS = ["web_pwa", "android", "backend", "minecraft_mod", "other"] as const;
export const Platform = z.enum(PLATFORMS);
export type Platform = z.infer<typeof Platform>;
